import React, { useEffect, useState, useCallback } from 'react';
import { AdminPageShell } from '../_components/AdminPageShell';
import { apiFetch } from '@/lib/apiFetch';
import {
  Loader2,
  Server,
  Activity,
  AlertTriangle,
  Globe,
  Clock,
  FileText,
} from 'lucide-react';

interface WorkerStatus {
  id: number;
  hostname: string;
  pid: number;
  active: number;
  alive: boolean;
  lastSeen: string;
}

interface QueueStatus {
  pending: number;
  running: number;
  done: number;
  failed: number;
  byType: Record<string, number>;
}

interface ErrorEntry {
  jobType: string;
  error: string;
  createdAt: string;
  attempts: number;
}

interface ScrapeEntry {
  id: string;
  sourceName: string;
  status: string;
  found: number;
  newArtifacts: number;
  startedAt: string;
  finishedAt: string;
  error: string | null;
}

interface LogData {
  workers: WorkerStatus[];
  queue: QueueStatus;
  recentErrors: ErrorEntry[];
  recentScrapes: ScrapeEntry[];
}

function timeAgo(iso: string): string {
  const s = Math.round((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return s + 's ago';
  if (s < 3600) return Math.round(s / 60) + 'm ago';
  if (s < 86400) return Math.round(s / 3600) + 'h ago';
  return Math.round(s / 86400) + 'd ago';
}

function duration(start: string, end?: string | null): string {
  const ms = new Date(end ?? Date.now()).getTime() - new Date(start).getTime();
  const s = Math.round(ms / 1000);
  if (s < 60) return s + 's';
  if (s < 3600) return Math.round(s / 60) + 'm ' + (s % 60) + 's';
  return Math.round(s / 3600) + 'h ' + Math.round((s % 3600) / 60) + 'm';
}

export const AdminLogsPage: React.FC = () => {
  const [data, setData] = useState<LogData | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const res = await apiFetch('/api/admin/logs');
      if (res.ok) {
        const json = await res.json();
        setData(json.data);
      }
    } catch {
      // Retry on next interval
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    return () => {};
  }, [fetchData]);

  useEffect(() => {
    const timer = setInterval(fetchData, 10000);
    return () => clearInterval(timer);
  }, [fetchData]);

  return (
    <AdminPageShell title="Log Monitor" sysAdminOnly>
      {loading && !data ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* ── Workers ── */}
          <Card>
            <h3 className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-3">
              <Server className="h-4 w-4" /> Workers
            </h3>
            <div className="space-y-2">
              {(data?.workers ?? []).map((w) => (
                <div
                  key={w.id}
                  className="flex items-center justify-between rounded-lg border border-gray-200 p-3"
                >
                  <div className="flex items-center gap-2">
                    <span
                      className={`h-2.5 w-2.5 rounded-full ${
                        w.alive ? 'bg-emerald-500' : 'bg-red-500'
                      }`}
                    />
                    <div>
                      <p className="text-sm font-medium text-gray-900">
                        {w.id === 1 ? 'Main Worker' : 'Scraper Worker'}
                      </p>
                      <p className="text-xs text-gray-500">
                        {w.hostname} · PID {w.pid}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-semibold text-gray-900">
                      {w.active} active
                    </p>
                    <p className="text-xs text-gray-400">
                      {timeAgo(w.lastSeen)}
                    </p>
                  </div>
                </div>
              ))}
              {(!data?.workers || data.workers.length === 0) && (
                <p className="text-sm text-gray-400 py-2">
                  No workers connected
                </p>
              )}
            </div>
          </Card>

          {/* ── Queue ── */}
          <Card>
            <h3 className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-3">
              <Activity className="h-4 w-4" /> Queue
            </h3>
            {data?.queue && (
              <>
                <div className="grid grid-cols-4 gap-3 mb-4">
                  {[
                    {
                      label: 'Pending',
                      value: data.queue.pending,
                      color: 'text-amber-600',
                    },
                    {
                      label: 'Running',
                      value: data.queue.running,
                      color: 'text-blue-600',
                    },
                    {
                      label: 'Done',
                      value: data.queue.done,
                      color: 'text-emerald-600',
                    },
                    {
                      label: 'Failed',
                      value: data.queue.failed,
                      color: 'text-red-600',
                    },
                  ].map((s) => (
                    <div
                      key={s.label}
                      className="rounded-lg border border-gray-200 bg-gray-50 p-2.5 text-center"
                    >
                      <p className={`text-lg font-bold ${s.color}`}>
                        {s.value.toLocaleString()}
                      </p>
                      <p className="text-[10px] font-medium uppercase text-gray-400">
                        {s.label}
                      </p>
                    </div>
                  ))}
                </div>
                <div className="space-y-1">
                  <p className="text-[10px] font-semibold uppercase text-gray-400">
                    By type (pending + running)
                  </p>
                  {Object.entries(data.queue.byType)
                    .sort(([, a], [, b]) => b - a)
                    .slice(0, 8)
                    .map(([type, count]) => (
                      <div
                        key={type}
                        className="flex items-center justify-between text-xs"
                      >
                        <span className="text-gray-600 font-mono">{type}</span>
                        <span className="font-semibold text-gray-900">
                          {count}
                        </span>
                      </div>
                    ))}
                </div>
              </>
            )}
          </Card>

          {/* ── Recent errors ── */}
          <Card>
            <h3 className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-3">
              <AlertTriangle className="h-4 w-4" /> Recent Errors
            </h3>
            <div className="max-h-72 overflow-y-auto space-y-1.5">
              {(data?.recentErrors ?? []).map((e, i) => (
                <div
                  key={i}
                  className="rounded-lg border border-red-100 bg-red-50 p-2.5"
                >
                  <div className="flex items-start justify-between gap-2">
                    <p className="text-xs font-medium text-red-800 break-all line-clamp-2">
                      {e.error}
                    </p>
                    <span className="shrink-0 text-[10px] text-red-400">
                      {e.attempts}/{e.attempts > 2 ? '3' : '3'} attempts
                    </span>
                  </div>
                  <div className="mt-1 flex items-center gap-2 text-[10px] text-red-400">
                    <span className="font-mono">{e.jobType}</span>
                    <span>·</span>
                    <span>{timeAgo(e.createdAt)}</span>
                  </div>
                </div>
              ))}
              {(!data?.recentErrors || data.recentErrors.length === 0) && (
                <p className="text-sm text-gray-400 py-2">No recent errors</p>
              )}
            </div>
          </Card>

          {/* ── Recent scrapes ── */}
          <Card>
            <h3 className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-3">
              <Globe className="h-4 w-4" /> Recent Scrapes
            </h3>
            <div className="max-h-72 overflow-y-auto space-y-1.5">
              {(data?.recentScrapes ?? []).map((s) => (
                <div
                  key={s.id}
                  className={`rounded-lg border p-2.5 ${
                    s.status === 'succeeded'
                      ? 'border-emerald-100 bg-emerald-50'
                      : s.status === 'failed'
                        ? 'border-red-100 bg-red-50'
                        : 'border-gray-100 bg-gray-50'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <p className="text-xs font-medium text-gray-900 truncate max-w-[60%]">
                      {s.sourceName}
                    </p>
                    <span
                      className={`text-[10px] font-semibold ${
                        s.status === 'succeeded'
                          ? 'text-emerald-600'
                          : s.status === 'failed'
                            ? 'text-red-600'
                            : 'text-blue-600'
                      }`}
                    >
                      {s.status}
                    </span>
                  </div>
                  <div className="mt-1 flex items-center gap-3 text-[10px] text-gray-400">
                    {s.found > 0 && (
                      <span className="flex items-center gap-1">
                        <FileText className="h-3 w-3" />
                        {s.found} found / {s.newArtifacts} new
                      </span>
                    )}
                    {s.startedAt && (
                      <span className="flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        {duration(s.startedAt, s.finishedAt)}
                      </span>
                    )}
                    <span>{timeAgo(s.startedAt)}</span>
                  </div>
                  {s.error && (
                    <p className="mt-1 text-[10px] text-red-400 truncate">
                      {s.error}
                    </p>
                  )}
                </div>
              ))}
              {(!data?.recentScrapes || data.recentScrapes.length === 0) && (
                <p className="text-sm text-gray-400 py-2">No recent scrapes</p>
              )}
            </div>
          </Card>
        </div>
      )}
    </AdminPageShell>
  );
};

function Card({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div
      className={`rounded-xl border border-gray-200 bg-white p-5 shadow-sm ${className ?? ''}`}
    >
      {children}
    </div>
  );
}
